import { current, Draft, isDraft } from "immer"

export class Updater<State, Action> {
  constructor(
    readonly update: (draft: Draft<State>, action: Action) => void
  ) { }

  /**
   * Run some code after running my `update` function.
   * @param body 
   * @returns 
   */
  then(body: (draft: Draft<State>) => void): Updater<State, Action> {
    const wrappedUpdate = this.update
    return new Updater((draft: Draft<State>, action: Action) => {
      wrappedUpdate(draft, action)
      body(draft)
    })
  }

  /**
   * Run some code after running my `update` function, if a value derived from my `State` was changed.
   * @param getter Get the value to monitor for changes.
   * @param body Perform some further mutations to my `State` when the value returned by `getter` changes.
   * @returns 
   */
  onChangeOf<Value>(
    getter: (draft: Draft<State>) => Value,
    body: (oldValue: Value, draft: Draft<State>) => void
  ): Updater<State, Action> {
    const wrappedUpdate = this.update
    return new Updater((draft: Draft<State>, action: Action) => {
      const oldValue = snapshotOf(getter(draft))
      wrappedUpdate(draft, action)
      const newValue = snapshotOf(getter(draft))
      if (!Object.is(oldValue, newValue)) {
        body(oldValue, draft)
      }
    })
  }
}

function snapshotOf(value: any): any {
  return isDraft(value) ? current(value) : value
}
